import { ViewChild, Input, OnInit, EventEmitter, Output, OnChanges } from '@angular/core';
import { Component } from '@angular/core';
import { LogoHandlerComponent } from 'src/app/components/file-handler/logo-handler.component';
import { capitalize, storageUrlToPath, downloadAndRenameFromUrl } from 'src/app/helpers/helpers';
import { UploadStatus, ImageType, ImageSubType, ImageUploaderDisplayTypes, imageUploaderDisplayTypes } from 'globals/store/files.model';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { mimeTypeToImageExtension } from 'src/app/helpers/extensions';
import { ImageUploaderResult } from 'globals/store/share.data';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { PhotoComponent } from '../photo';
import { DeleteButtonComponent } from '../buttons/delete-button.component';
import { environment } from '../../../environments/environment';

const ERROR_DISPLAY_MS = 3000

// this component no longer deletes previous images (so in the case of immediate updates, it never deletes anything, just sets the path to be empty).  It will only delete temporary photos.  This is so that those images will remain in things liked saved timelines.
@Component({
    selector: 'image-uploader',
    templateUrl: './image-uploader.html',
    styleUrls: ['./image-uploader.scss'],
    standalone: true,
    imports: [LogoHandlerComponent, MatProgressSpinnerModule, CommonModule, PhotoComponent, DeleteButtonComponent],
})
export class ImageUploaderComponent implements OnInit, OnChanges {
  @Input() uploadText = 'UPLOAD PHOTO'
  @Input() placeholder = 'Add photo'
  @Input() uniqueFormIdentifier = 'desktop'

  @Input() photoUrl
  @Input() thumbUrl
  @Input() maxSize = 1024*1024*10 // 10 MB
  @Input() type: ImageType
  @Input() subtype: ImageSubType
  @Input() id: string
  @Input() eventId: string
  @Input() accountId: string
  @Input() noThumb = false

  @Input() isTemplate: boolean = false // if it is a template, eventId is accountId
  @Input() displayType: keyof ImageUploaderDisplayTypes = imageUploaderDisplayTypes.card
  displayTypes = imageUploaderDisplayTypes
  
  @Input() deleteTitle
  @Input() deleteText
  @Input() public = false
  @Input() publishCode: string = null
  @Input() hints = ['JPG, PNG, GIF or SVG', '10MB maximum']
  @Input() saveName
  @Input() immediate = false
  @Output() newUrls: EventEmitter<ImageUploaderResult> = new EventEmitter()
  @ViewChild('logoHandler', { static: true }) logoHandler: LogoHandlerComponent;
  imageChanging = false
  downloadingImage = false
  toDeleteUrls = []
  tempPhotoUrls = []
  finalDeleteTitle = ''
  finalDeleteText = ''
  tempUrl: string = null
  tempThumbUrl: string = null
  error = ''
  api = `${environment.functionsURL}api/app` // need to be able to set to "v1" for public
  @Input() isLogo = false


  constructor( private http: HttpClient) { }
  ngOnInit() {
    if (this.displayType !== 'text') {
      if (!this.eventId) console.log("THERE IS NO EVENT ID BEING PASSED INTO IMAGE UPLOADER.  IT NEEDS ONE TO INITIALIZE SAVE PATHS.  IF YOU SEE THIS, MAKE SURE TO PUT AN ngIf ON <image-uploader> IN THE CALLING COMPONENT")
      this.finalDeleteTitle = this.deleteTitle ? this.deleteTitle : `Delete ${capitalize(this.type)} ${capitalize(this.subtype)}`,
      this.finalDeleteText = this.deleteText ? this.deleteText : `Are you sure you want to delete this ${this.type}'s ${this.subtype}?${this.immediate ? '' : " (Changes won't be final unless you hit save)"}`
      this.tempUrl = this.photoUrl
      this.tempThumbUrl = this.thumbUrl
    }
  }

  ngOnChanges(changes) {
    if (this.immediate) {
      if (changes.photoUrl) this.tempUrl = changes.photoUrl.currentValue
      if (changes.thumbUrl) this.tempThumbUrl = changes.thumbUrl.currentValue
    }
  }

  async downloadImage(imageUrl: string) {
    this.downloadingImage = true
    try {
      const path = storageUrlToPath(imageUrl)
      const headers = new HttpHeaders()
      headers.append('Accept', 'application/json')
      const response: any = await this.http.post(`${this.api}/get-image-metadata`, { path }, { headers }).toPromise()
      console.log({ response })
      const { metadata }: { metadata: any } = response
      const mimeType: string = metadata.ContentType
      const extension = mimeTypeToImageExtension[mimeType] || "unknown"
      if (!mimeTypeToImageExtension[mimeType]) {
        console.log("MISSING extension for mimeType: " + mimeType)
      }
      const saveName = `${!this.saveName ? this.type : this.saveName.split(" ").filter(s => s !== " ").join("-")}-${this.subtype}.${extension}`
      console.log({ saveName })
      downloadAndRenameFromUrl(imageUrl, saveName)
    } catch (error) {
      console.log(error)
    } finally {
      this.downloadingImage = false
    }
  }

  addNewPhotoUrls({ photoUrl, thumbUrl, originalName }: ImageUploaderResult) {
    if (this.immediate) {
      this.tempUrl = photoUrl
      this.tempThumbUrl = thumbUrl
      console.log(this.uploadText)
      this.newUrls.emit({ photoUrl, thumbUrl, originalName })
      return
    }

    if (this.tempUrl && this.tempUrl !== this.photoUrl) this.toDeleteUrls.push(this.tempUrl)
    if (this.tempThumbUrl && this.tempThumbUrl !== this.thumbUrl) this.toDeleteUrls.push(this.tempThumbUrl)
    this.tempPhotoUrls.push(photoUrl)
    this.tempPhotoUrls.push(thumbUrl)
    this.tempUrl = photoUrl
    this.tempThumbUrl = thumbUrl
  }

  deleteTempPhotos() {
    // console.log({ tempPhotoUrls: this.tempPhotoUrls })
    if (this.public) return // no permissions to do this.  Just causes errors.  Of course
    this.tempPhotoUrls.forEach(photoUrl => this.logoHandler.deletePhotoFromUrl(photoUrl))
  }

  deleteDeletedPhotos() { // returns true if one or more photos were deleted (say, if a user uploaded a photo and replaced the old one, deleted the new one).  Pull this.tempUrl for the new photo url, even if it is null (replacing the old value possibly)
    // console.log({ toDeleteUrls: this.toDeleteUrls })
    if (this.public) return this.toDeleteUrls.length || this.tempUrl // cannot delete from public url, no permissions to do so.
    if (this.toDeleteUrls.length) {
      this.toDeleteUrls.forEach(photoUrl => this.logoHandler.deletePhotoFromUrl(photoUrl))
    }
    return this.toDeleteUrls.length || this.tempUrl // <== return true if there is a new photo (or even a void photo where there was one before)
  }

  setError(error) {
    this.error = error
    setTimeout(() => this.error = null, ERROR_DISPLAY_MS)
  }

  updateUploadStatus(status: UploadStatus) {
    switch (status.state) { 
      case 'uploading':
        this.imageChanging = true
        break
      case 'uploaded':
        this.imageChanging = false
        this.addNewPhotoUrls({ photoUrl: status.savePath, thumbUrl: status.thumbSavePath, originalName: status.originalName })
        break
      case 'invalidFile':
        this.imageChanging = false
        this.setError('Invalid file detected. Please try another.')
        console.log('Invalid file detected')
        break
      case 'error':
        this.imageChanging = false
        this.setError('Something went wrong.  Please try again.')
        break
      case 'exceedsMaxSize':
        this.setError('Your image exceeds the maximum size')
        break
      default:
        console.log({ status })
        break
    }
  }

  deleteImage() {
    if (this.immediate) {
      this.newUrls.emit(null)
      this.tempUrl = null
      this.tempThumbUrl = null
      return
    }
    if (this.tempUrl && this.tempUrl !== this.photoUrl) this.toDeleteUrls.push(this.tempUrl)
    if (this.tempThumbUrl && this.tempThumbUrl !== this.thumbUrl) this.toDeleteUrls.push(this.tempThumbUrl)
    this.tempUrl = null
    this.tempThumbUrl = null
  }
}
